/*
 * Export one report as a self-contained HTML file (NEXUS-1384 dashboard v2) — opens offline in
 * any browser: title, who pinned it, when it ran, the active filters, the chart (the live SVG the
 * card is showing) and the grid exactly as filtered + sorted. The exported grid stays sortable by
 * clicking a header (tiny inline script, no network).
 */

import type { ReportColumn, ReportResult } from './reports';
import { formatCell, linkFor, type Row } from '../components/DataGrid';

const esc = (s: unknown) =>
  String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);

export interface ExportInput {
  result: ReportResult;
  title?: string | null;
  rows: Row[];
  totalRows: number;
  filters: Array<{ label: string; values: string[] }>;
  sortLabel?: string | null;
  chartSvg?: string | null;
  pinnedBy?: string | null;
  pinnedAt?: string | null;
  origin?: string;
}

export function buildReportHtml(x: ExportInput): string {
  const title = x.title || x.result.title;
  const ran = new Date(x.result.generatedAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  const meta: string[] = [];
  if (x.result.subtitle) meta.push(esc(x.result.subtitle));
  meta.push(`Data as of ${esc(ran)}`);
  if (x.pinnedBy || x.pinnedAt) {
    meta.push(
      `Pinned by ${esc(x.pinnedBy || 'staff')}${x.pinnedAt ? ` on ${esc(new Date(x.pinnedAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }))}` : ''}`,
    );
  }
  const filterLine = x.filters.length
    ? `<p class="filters"><b>Filters:</b> ${x.filters.map((f) => `${esc(f.label)} = ${f.values.map(esc).join(' or ')}`).join(' · ')} — ${x.rows.length} of ${x.totalRows} rows</p>`
    : '';
  const cols: ReportColumn[] = x.result.columns;
  const head = cols
    .map((c, i) => `<th data-i="${i}" data-t="${c.type}" class="${c.type === 'number' ? 'num' : ''}">${esc(c.label)} <span class="s">⇅</span></th>`)
    .join('');
  const body = x.rows
    .map(
      (r) =>
        `<tr>${cols
          .map((c) => {
            const raw = r[c.key];
            const sortKey = c.type === 'number' ? Number(raw ?? 0) : c.type === 'datetime' || c.type === 'date' ? Date.parse(String(raw ?? '')) || 0 : String(raw ?? '').toLowerCase();
            const text = esc(formatCell(c, raw));
            const href = linkFor(c, r);
            const cell = href && x.origin ? `<a href="${esc(x.origin + href)}">${text}</a>` : text;
            return `<td data-v="${esc(sortKey)}" class="${c.type === 'number' ? 'num' : ''}">${cell}</td>`;
          })
          .join('')}</tr>`,
    )
    .join('\n');
  const chart = x.chartSvg ? `<div class="chart">${x.chartSvg}</div>` : '';
  return `<!doctype html>
<html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1">
<title>${esc(title)}</title>
<style>
:root{color-scheme:light}
body{font:14px/1.45 -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;color:#101c42;margin:0;background:#f5f7fb}
main{max-width:1100px;margin:0 auto;padding:28px 20px}
.card{background:#fff;border-radius:12px;box-shadow:0 1px 3px rgba(16,28,66,.08);padding:20px 22px}
h1{font-size:22px;margin:0 0 4px}
.meta{color:#5b6475;margin:0 0 14px;font-size:13px}
.filters{background:#eef7fc;border-radius:8px;padding:8px 12px;font-size:13px}
.chart{margin:8px 0 16px;overflow-x:auto}.chart svg{max-width:100%;height:auto}
table{border-collapse:collapse;width:100%;font-size:13px}
th,td{padding:7px 10px;border-bottom:1px solid #e6e9ef;text-align:left;white-space:nowrap}
th{background:#f4f6f9;cursor:pointer;user-select:none;position:sticky;top:0}
th .s{color:#9aa3b2;font-size:11px}
td.num,th.num{text-align:right;font-variant-numeric:tabular-nums}
tr:hover td{background:#fafbfd}
a{color:#0177b5;text-decoration:none}
footer{color:#8a93a3;font-size:12px;margin-top:14px}
@media print{body{background:#fff}.card{box-shadow:none}}
</style></head>
<body><main><div class="card">
<h1>${esc(title)}</h1>
<p class="meta">${meta.join(' · ')}</p>
${filterLine}
${chart}
<table id="g"><thead><tr>${head}</tr></thead><tbody>
${body}
</tbody></table>
<footer>Exported ${esc(new Date().toLocaleString())} from the AquaStar admin console${x.sortLabel ? ` · sorted by ${esc(x.sortLabel)}` : ''}. Click a column header to sort.</footer>
</div></main>
<script>
(function(){var t=document.getElementById('g'),b=t.tBodies[0],d={};
t.tHead.addEventListener('click',function(e){var th=e.target.closest('th');if(!th)return;var i=+th.dataset.i,txt=th.dataset.t==='text';
d[i]=d[i]==='asc'?'desc':'asc';var m=d[i]==='asc'?1:-1;
var rs=[].slice.call(b.rows);rs.sort(function(a,c){var x=a.cells[i].dataset.v,y=c.cells[i].dataset.v;
return (txt?x.localeCompare(y):(+x)-(+y))*m;});rs.forEach(function(r){b.appendChild(r)});
[].forEach.call(t.tHead.rows[0].cells,function(h){h.querySelector('.s').textContent='⇅'});th.querySelector('.s').textContent=m>0?'▲':'▼';});})();
</script>
</body></html>`;
}

export function slugify(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 60) || 'report';
}

export function downloadHtml(filename: string, html: string): void {
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

/** The recharts SVG inside a container, with inline sizing so it renders standalone. */
export function chartSvgFrom(el: HTMLElement | null): string | null {
  const svg = el?.querySelector('svg.recharts-surface') as SVGSVGElement | null;
  if (!svg) return null;
  const clone = svg.cloneNode(true) as SVGSVGElement;
  const w = svg.getAttribute('width') || String(svg.clientWidth || 800);
  const h = svg.getAttribute('height') || String(svg.clientHeight || 300);
  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  clone.setAttribute('viewBox', `0 0 ${w} ${h}`);
  clone.setAttribute('font-family', '-apple-system, Segoe UI, Roboto, sans-serif');
  const legend = el?.querySelector('.recharts-legend-wrapper');
  const legendHtml = legend ? `<div style="font-size:12px;text-align:center">${legend.innerHTML}</div>` : '';
  return clone.outerHTML + legendHtml;
}
